import { ExternalLink } from "lucide-react";
import { RedactedText } from "@/components/redaction/RedactedText";
import { AUTHOR } from "@/data/secrets";

/** The author's redacted personnel file. */
export const PersonnelFile = ({ variant = "dark" }) => {
  const paper = variant === "paper";
  const ink = paper ? "text-[hsl(var(--ink))]" : "text-foreground";
  const soft = paper ? "text-[hsl(220_12%_38%)]" : "text-muted-foreground";
  const body = paper ? "text-[hsl(220_14%_26%)]" : "text-[hsl(210_12%_80%)]";
  const rule = paper ? "border-[hsl(36_16%_80%)]" : "border-border";

  return (
    <div data-testid="personnel-file">
      <div className={`flex flex-wrap items-center gap-3 border-b pb-3 ${rule}`}>
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.2em] ${soft}`}
        >
          Personnel file · form PF-00
        </span>
        <span className="stamp stamp-open">{AUTHOR.status}</span>
      </div>

      <h3 className={`mt-5 text-3xl font-semibold leading-none ${ink}`}>
        {AUTHOR.displayName}
      </h3>
      <p
        className={`mt-2 font-mono text-[11px] uppercase tracking-[0.16em] ${soft}`}
      >
        {AUTHOR.role}
      </p>

      <dl className="mt-6">
        {AUTHOR.fields.map((f, i) => (
          <div
            key={f.k}
            className={`flex flex-wrap items-baseline justify-between gap-3 py-2.5 ${
              i > 0 ? `border-t ${rule}` : ""
            }`}
          >
            <dt
              className={`font-mono text-[10px] uppercase tracking-[0.16em] ${soft}`}
            >
              {f.k}
            </dt>
            <dd className={`font-mono text-[11px] ${body}`}>
              <RedactedText text={f.v} />
            </dd>
          </div>
        ))}
      </dl>

      <div className={`mt-6 border-t pt-5 ${rule}`}>
        <p
          className={`font-mono text-[10px] uppercase tracking-[0.18em] ${soft}`}
        >
          Notes appended
        </p>
        {AUTHOR.bio ? (
          <p className={`mt-3 text-[14.5px] leading-relaxed ${body}`}>
            {AUTHOR.bio}
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {AUTHOR.notes.map((n, i) => (
              <li
                key={i}
                className={`flex gap-3 text-[14.5px] leading-relaxed ${body}`}
              >
                <span
                  className={`mt-[9px] h-1 w-1 shrink-0 rounded-full ${
                    paper ? "bg-[hsl(220_12%_55%)]" : "bg-[hsl(174_72%_46%)]"
                  }`}
                />
                {n}
              </li>
            ))}
          </ul>
        )}
      </div>

      {AUTHOR.links?.length > 0 && (
        <div className={`mt-6 border-t pt-5 ${rule}`}>
          <p
            className={`font-mono text-[10px] uppercase tracking-[0.18em] ${soft}`}
          >
            Follow the signal
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {AUTHOR.links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`author-link-${link.label.toLowerCase()}`}
                className={
                  paper
                    ? "inline-flex items-center gap-2 rounded-[10px] border border-[hsl(36_18%_70%)] bg-white px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ink))] transition-colors duration-150 hover:bg-[hsl(38_38%_96%)]"
                    : "inline-flex items-center gap-2 rounded-[10px] border border-border bg-[hsl(220_18%_8%)] px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground transition-colors duration-150 hover:border-[hsl(174_72%_42%/0.5)] hover:bg-[hsl(220_16%_12%)]"
                }
              >
                {link.label}
                <span className={soft}>{link.handle}</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
